import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import {
  LifecycleTimeline,
  LifecycleUnitSummary,
  fetchTimeline,
  fetchTimelineByListing,
  fetchTimelineUnits,
} from "./DataServices/EventHistoryDataService";
import TimelineFlowCanvas from "./TimelineFlowCanvas";
import TimelineGroupPanel, { groupKey } from "./TimelineGroupPanel";

const PropertyTimeline: React.FC = () => {
  const { unitId, listingId } = useParams();
  const navigate = useNavigate();

  const [units, setUnits] = useState<LifecycleUnitSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [timeline, setTimeline] = useState<LifecycleTimeline | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [error, setError] = useState("");

  const [selectedCycleId, setSelectedCycleId] = useState("");
  const [selectedGroupKey, setSelectedGroupKey] = useState("tenant");

  const timelineReloadRef = React.useRef<() => void>(() => {});

  useEffect(() => {
    const loadUnits = async () => {
      try {
        const data = await fetchTimelineUnits();
        setUnits(data);
        if (!unitId && !listingId && data.length > 0) {
          navigate(`/timeline/${data[0].unitId}`, { replace: true });
        }
      } catch (e) {
        console.error("Error loading timeline units:", e);
        setError("Failed to load apartments.");
      } finally {
        setLoading(false);
      }
    };

    loadUnits();
    // Only the initial list load — the selected unit is handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!unitId && !listingId) return;

    const loadTimeline = async (preserveSelection = false) => {
      setTimelineLoading(true);
      setError("");
      try {
        const data = listingId
          ? await fetchTimelineByListing(listingId)
          : await fetchTimeline(unitId!);
        setTimeline(data);
        if (!preserveSelection) {
          setSelectedCycleId(data.cycles[0]?.id ?? "");
          setSelectedGroupKey("tenant");
        }
      } catch (e) {
        console.error("Error loading timeline:", e);
        setTimeline(null);
        setError("Failed to load this apartment's timeline.");
      } finally {
        setTimelineLoading(false);
      }
    };

    loadTimeline();
    timelineReloadRef.current = () => loadTimeline(true);
  }, [unitId, listingId]);

  const filteredUnits = useMemo(() => {
    const search = searchTerm.toLowerCase();
    return units.filter((u) =>
      `${u.apartmentTitle} ${u.building ?? ""} ${u.unitName ?? ""}`
        .toLowerCase()
        .includes(search),
    );
  }, [units, searchTerm]);

  const cycle = useMemo(
    () =>
      timeline?.cycles.find((c) => c.id === selectedCycleId) ??
      timeline?.cycles[0] ??
      null,
    [timeline, selectedCycleId],
  );

  // The tenancy is the default view; fall back to the first enquirer on a rental-only cycle.
  const group = useMemo(() => {
    if (!cycle) return null;
    return (
      cycle.groups.find((g) => groupKey(g) === selectedGroupKey) ??
      cycle.groups[0] ??
      null
    );
  }, [cycle, selectedGroupKey]);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[110rem] px-1 py-4">
      <div className="mb-4">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          Property Lifecycle
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Follow every apartment through its renewal, move-out and rental milestones.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:h-[calc(100vh-13rem)] lg:min-h-[32rem] lg:grid-cols-[16rem_minmax(0,1fr)_19rem]">
        {/* Apartments */}
        <aside className="flex h-64 min-h-0 flex-col rounded-xl border border-gray-200 bg-white shadow-sm lg:h-auto">
          <div className="border-b border-gray-200 px-3 py-2.5">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Apartments ({units.length})
            </p>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search apartments..."
                className="block w-full rounded-md border-0 py-1.5 pl-8 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600"
              />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {filteredUnits.length === 0 ? (
              <p className="px-2 py-6 text-center text-xs text-gray-500">
                No apartments found.
              </p>
            ) : (
              filteredUnits.map((u) => {
                // Arriving via /timeline/by-listing/:listingId (e.g. the "View Timeline" action
                // on a listing) has no unitId param — fall back to the resolved timeline's own
                // unit id, which is the real unit id whenever the listing maps to one.
                const active = u.unitId === (unitId ?? timeline?.unit.id);

                return (
                  <button
                    key={u.unitId}
                    type="button"
                    onClick={() => navigate(`/timeline/${u.unitId}`)}
                    className={`mb-1.5 flex w-full items-start gap-2 rounded-md border px-2.5 py-2 text-left transition-colors ${
                      active
                        ? "border-blue-300 bg-blue-50"
                        : "border-gray-200 hover:border-blue-200 hover:bg-gray-50"
                    }`}
                  >
                    <BuildingOfficeIcon
                      className={`mt-0.5 h-4 w-4 flex-shrink-0 ${
                        active ? "text-blue-600" : "text-gray-400"
                      }`}
                    />
                    <span className="min-w-0 flex-1">
                      <span
                        className={`block truncate text-xs font-semibold ${
                          active ? "text-blue-700" : "text-gray-900"
                        }`}
                      >
                        {u.apartmentTitle}
                      </span>
                      <span className="block truncate text-[10px] text-gray-500">
                        {[
                          u.bedrooms != null ? `${u.bedrooms} bd` : null,
                          u.line ? `Line ${u.line}` : null,
                          u.building,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* Flows */}
        {timelineLoading ? (
          <section className="flex h-56 min-h-0 items-center justify-center rounded-xl border border-gray-200 bg-white shadow-sm lg:h-auto">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-500"></div>
          </section>
        ) : !timeline || !cycle ? (
          <section className="flex h-56 min-h-0 items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 text-center lg:h-auto">
            <p className="text-sm text-gray-500">
              {error || "This apartment has no lifecycle history yet."}
            </p>
          </section>
        ) : (
          <TimelineFlowCanvas
            unit={timeline.unit}
            cycles={timeline.cycles}
            cycle={cycle}
            onSelectCycle={(cycleId) => {
              setSelectedCycleId(cycleId);
              setSelectedGroupKey("tenant");
            }}
            group={group}
            onRentedProspectChanged={() => timelineReloadRef.current()}
          />
        )}

        {/* Groups */}
        {timeline && cycle && !timelineLoading ? (
          <TimelineGroupPanel
            unit={timeline.unit}
            cycle={cycle}
            selectedGroupKey={group ? groupKey(group) : "tenant"}
            onSelectGroup={setSelectedGroupKey}
          />
        ) : (
          <aside className="hidden rounded-xl border border-gray-200 bg-white shadow-sm lg:block" />
        )}
      </div>
    </div>
  );
};

export default PropertyTimeline;
